import { Box, Center, HStack, Spinner, Text } from "@chakra-ui/react";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import React, { memo } from "react";
import {
  useCurrentlyRenderedData,
  useVirtuosoLocation,
  VirtuosoMessageList,
  VirtuosoMessageListLicense,
  VirtuosoMessageListProps,
} from "@virtuoso.dev/message-list";
import colors from "app/colors";
import usePagedMessageList, {
  ScrollDirection,
  VirtuosoMessageListContext,
} from "modules/hooks/use-paged-message-list";
import { NEXT_PUBLIC_VIRTUOSO_LICENSE_KEY } from "app/client";
import SummarizeConversationButton from "../_shared/summarize-conversation-button";
import MessageDateSeparator from "../messages/message-date-separator";
import ScrollToEdgeButton from "../_shared/scroll-to-edge-button";

interface IProps {
  messageThread: MessageThreadFragment;
  setScrollDirection?: (scrollDirection: ScrollDirection) => void;
}

const MessageThreadMessageList: React.FC<IProps> = ({
  messageThread,
  setScrollDirection,
}) => {
  const {
    virtuosoRef,
    virtuosoMessageListContext,
    onScroll,
    computeItemKey,
    renderMessage,
  } = usePagedMessageList({
    messageThread,
    setScrollDirection,
    startAtTop: false,
  });

  return (
    <VirtuosoMessageListLicense licenseKey={NEXT_PUBLIC_VIRTUOSO_LICENSE_KEY}>
      <VirtuosoMessageList<MessageFragment, VirtuosoMessageListContext>
        id={`scroll-${messageThread.id}`}
        className="message-list-scroller"
        ref={virtuosoRef}
        style={{ flex: 1 }}
        onScroll={onScroll}
        computeItemKey={computeItemKey}
        ItemContent={renderMessage}
        increaseViewportBy={800}
        context={virtuosoMessageListContext}
        Header={Header}
        Footer={Footer}
        StickyHeader={StickyHeader}
        StickyFooter={StickyFooter}
        EmptyPlaceholder={EmtpyPlaceholder}
        enforceStickyFooterAtBottom
      />
    </VirtuosoMessageListLicense>
  );
};

interface IComponentProps {
  context: VirtuosoMessageListContext;
}

const Header = ({ context }: IComponentProps) => {
  if (!context) return null;
  if (context.isPrepending === true) {
    return (
      <Box h={8}>
        <Center>
          <Spinner />
        </Center>
      </Box>
    );
  }
  return null;
};

const EmtpyPlaceholder: VirtuosoMessageListProps<
  MessageFragment,
  VirtuosoMessageListContext
>["EmptyPlaceholder"] = () => (
  <Center h="100%">
    <Spinner />
  </Center>
);

const StickyFooter: VirtuosoMessageListProps<
  MessageFragment,
  VirtuosoMessageListContext
>["StickyFooter"] = ({ context }) => {
  const location = useVirtuosoLocation();
  return (
    <HStack pb={2} pr={2} w="full" justifyContent="flex-end" spacing={4}>
      <SummarizeConversationButton
        messageThreadId={context.messageThreadId}
        type={context.type}
        isAtBottom={location.isAtBottom}
      />
      <ScrollToEdgeButton
        location={location}
        hasLastMessage={context.hasLastMessage}
        hasFirstMessage={context.hasFirstMessage}
        initializeMessageList={context.initializeMessageList}
      />
    </HStack>
  );
};

const StickyHeader: VirtuosoMessageListProps<
  MessageFragment,
  VirtuosoMessageListContext
>["StickyHeader"] = () => {
  const firstItem = useCurrentlyRenderedData<MessageFragment>()[0] as
    | MessageFragment
    | undefined;

  //in some cases we do not want to show date separator at the top (e.g., for task threads)

  if (firstItem?.type === MessageType.Task) {
    return null;
  }

  if (
    firstItem?.type === MessageType.External &&
    ["HAPPYCO_WORK_ORDER_MESSAGE"].includes(firstItem?.subType || "")
  ) {
    return null;
  }

  return (
    <Box w="full" position="absolute" top={1}>
      <MessageDateSeparator date={firstItem?.createTime} displayContainer />
    </Box>
  );
};

const Footer = ({ context }: IComponentProps) => {
  if (context.isAppending === true) {
    return (
      <Box h={8}>
        <Center>
          <Spinner />
        </Center>
      </Box>
    );
  }
  if (context.isMeRemoved) {
    return (
      <Box h="32px">
        <HStack
          h="1px"
          backgroundColor={colors.gray30}
          w="full"
          alignItems="center"
          mb={2}
        >
          <Center w="full">
            <Text
              fontSize={12}
              fontWeight={700}
              zIndex={1}
              backgroundColor={colors.white}
              color={colors.gray30}
              rounded="full"
              px={4}
              w={180}
              textAlign="center"
            >
              END OF CONVERSATION
            </Text>
          </Center>
        </HStack>
      </Box>
    );
  }
  return null;
};

export default memo(MessageThreadMessageList);
